// ══════════════════════════════════════════════════════════════════
//  ED CRM - Flow Config Service (Bloco Fluxo, Config/42) — núcleo
// ══════════════════════════════════════════════════════════════════
//  Regras dos campos de configuração de fluxo (stage_role,
//  closer_pipeline_id, cadence[].actions[].on_complete).
//  Recebe o repositório via porta (DI) — não conhece Supabase.
//
//  Todas as funções de leitura têm fallback gracioso — nunca lançam.
// ══════════════════════════════════════════════════════════════════

import type { OnCompleteAction } from '@models';
import type { FlowActivity } from '@/types/activity';
import type {
  IFlowConfigRepository,
  PipelineFull,
  StageRoleInfo,
  MeetingStatus,
} from '../ports/IFlowConfigRepository';

// Re-exporta tipos de resultado consumidos pela UI.
export type { StageRoleInfo, PipelineFull } from '../ports/IFlowConfigRepository';

// ─── Tipos internos de parsing (jsonb cadence) ──────────────────
interface OnCompleteConfig {
  question?: string;
  yes_stage_id?: string | null;
  no_stage_id?: string | null;
  stage_id?: string | null;
  [key: string]: unknown;
}

interface CadenceAction {
  type?: string;
  title?: string;
  on_complete?: OnCompleteConfig;
  [key: string]: unknown;
}

interface CadenceBlock {
  stage_name?: string;
  actions?: CadenceAction[];
  [key: string]: unknown;
}

export interface ActionOnComplete {
  question: string;
  yes_stage_id: string | null;
  no_stage_id: string | null;
  yes_stage_name: string | null;
  no_stage_name: string | null;
  lead_name: string | null;
}

// ─── Dispatcher das 5 ações on_complete (Config/50) ─────────────
export interface OnCompletePlan {
  kind: 'noop' | 'dialog_yes_no' | 'execute';
  action?: OnCompleteAction;
  dialog?: {
    question: string;
    yes_stage_name: string | null;
    no_stage_name: string | null;
    lead_name: string | null;
  };
}

export interface ExecuteResult {
  ok: boolean;
  summary?: string;
  error?: string;
}

// ─── Service ────────────────────────────────────────────────────
export class FlowConfigService {
  constructor(private readonly repo: IFlowConfigRepository) {}

  // ── Stage roles ───────────────────────────────────────────────
  getStageByRole = async (
    pipelineId: string | null | undefined,
    role: string | null | undefined,
  ): Promise<StageRoleInfo | null> => {
    if (!pipelineId || !role) return null;
    return this.repo.getStageByRole(pipelineId, role);
  };

  setStageRole = async (stageId: string, role: string | null): Promise<void> => {
    if (!stageId) throw new Error('stageId obrigatório');
    const value = (role === '' || role == null) ? null : role;
    await this.repo.setStageRole(stageId, value);
  };

  setStageEmpresaGate = async (stageId: string, value: boolean): Promise<void> => {
    if (!stageId) throw new Error('stageId obrigatório');
    await this.repo.setStageEmpresaGate(stageId, value === true);
  };

  // ── Closer pipeline vinculado ─────────────────────────────────
  getCloserPipeline = async (
    sdrPipelineId: string | null | undefined,
  ): Promise<PipelineFull | null> => {
    if (!sdrPipelineId) return null;

    const sdr = await this.repo.getPipelineBasic(sdrPipelineId);
    if (!sdr) return null;

    // 1) FK explícita
    if (sdr.closer_pipeline_id) {
      const byFk = await this.repo.getActivePipelineById(sdr.closer_pipeline_id);
      if (byFk) return byFk;
    }

    // 2) Fallback: mesmo funnel
    if (sdr.funnel_id && sdr.workspace_id) {
      const byFunnel = await this.repo.getCloserPipelineByFunnel(sdr.workspace_id, sdr.funnel_id);
      if (byFunnel) return byFunnel;
    }

    // 3) Fallback: qualquer Closer ativo do workspace
    if (!sdr.workspace_id) return null;
    return this.repo.getAnyActiveCloserPipeline(sdr.workspace_id);
  };

  setCloserPipeline = async (
    sdrPipelineId: string,
    closerPipelineId: string | null,
  ): Promise<void> => {
    if (!sdrPipelineId) throw new Error('sdrPipelineId obrigatório');
    await this.repo.setCloserPipeline(sdrPipelineId, closerPipelineId || null);
  };

  // ── Fluxo "ao concluir" por atividade da cadência ─────────────
  getActionOnComplete = async (
    cardId: string | null | undefined,
    activityType: string,
    activityTitle: string,
  ): Promise<ActionOnComplete | null> => {
    if (!cardId) return null;

    try {
      // 1) Card → pipeline_id, stage_name, lead.nome
      const card = await this.repo.getCardOnCompleteInfo(cardId);
      if (!card?.pipeline_id) return null;

      const stageName = card.stage_name;
      const leadName  = card.lead_name || null;
      if (!stageName) return null;

      // 2) Pipeline cadence (jsonb)
      const raw = await this.repo.getPipelineCadence(card.pipeline_id);
      const cadence: CadenceBlock[] = Array.isArray(raw) ? (raw as CadenceBlock[]) : [];

      // 3) Stage block + action por (type, title)
      const block = cadence.find(c => c?.stage_name === stageName);
      const actions: CadenceAction[] = block && Array.isArray(block.actions) ? block.actions : [];
      const action = actions.find(a =>
        a?.type === activityType && a?.title === activityTitle,
      );

      const onComplete = action?.on_complete;
      if (!onComplete || !onComplete.question) return null;

      // 4) Resolve nomes dos stages alvo (para exibir no dialog)
      const stageIds = [onComplete.yes_stage_id, onComplete.no_stage_id]
        .filter((id): id is string => !!id);
      let nameById: Record<string, string> = {};
      if (stageIds.length > 0) {
        const stages = await this.repo.getStageNamesByIds(stageIds);
        nameById = Object.fromEntries(stages.map(s => [s.id, s.name]));
      }

      return {
        question: String(onComplete.question || ''),
        yes_stage_id: onComplete.yes_stage_id || null,
        no_stage_id:  onComplete.no_stage_id  || null,
        yes_stage_name: (onComplete.yes_stage_id && nameById[onComplete.yes_stage_id]) || null,
        no_stage_name:  (onComplete.no_stage_id && nameById[onComplete.no_stage_id]) || null,
        lead_name: leadName,
      };
    } catch {
      return null;
    }
  };

  resolveOnCompletePlan = async (activity: FlowActivity): Promise<OnCompletePlan> => {
    if (!activity?.id) return { kind: 'noop' };
    let action: OnCompleteAction | null = activity.on_complete_action || null;

    // Compat: se atividade antiga não tem action, tenta lookup no template.
    if (!action && activity.card_id) {
      const legacy = await this.getActionOnComplete(
        activity.card_id, activity.type || '', activity.title || '',
      ).catch(() => null);
      if (legacy?.question) {
        action = {
          type: 'change_stage',
          question: legacy.question,
          payload: {
            yes_stage_id: legacy.yes_stage_id,
            no_stage_id:  legacy.no_stage_id,
          },
        };
      }
    }

    if (!action) return { kind: 'noop' };

    // change_stage com pergunta SIM/NÃO → abre dialog
    if (action.type === 'change_stage' && action.question) {
      const names: { yes_stage_name: string | null; no_stage_name: string | null; lead_name: string | null } = {
        yes_stage_name: null, no_stage_name: null, lead_name: null,
      };
      const payload = action.payload as OnCompleteConfig;
      const stageIds = [payload?.yes_stage_id, payload?.no_stage_id]
        .filter((id): id is string => !!id);
      if (stageIds.length > 0) {
        const stages = await this.repo.getStageNamesByIds(stageIds);
        const byId: Record<string, string> = Object.fromEntries(stages.map(s => [s.id, s.name]));
        names.yes_stage_name = (payload?.yes_stage_id && byId[payload.yes_stage_id]) || null;
        names.no_stage_name  = (payload?.no_stage_id && byId[payload.no_stage_id]) || null;
      }
      if (activity.lead_id) {
        names.lead_name = await this.repo.getLeadName(activity.lead_id);
      }
      return {
        kind: 'dialog_yes_no',
        action,
        dialog: { question: action.question, ...names },
      };
    }

    return { kind: 'execute', action };
  };

  // answer: 'yes' | 'no' | undefined. Usado apenas para change_stage com pergunta.
  executeOnCompleteAction = async (
    activity: FlowActivity,
    action: OnCompleteAction,
    answer?: 'yes' | 'no',
  ): Promise<ExecuteResult> => {
    if (!activity?.id || !action?.type) return { ok: true, summary: 'no-op' };

    const payload = (action.payload || {}) as Record<string, unknown>;
    const asId = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

    try {
      switch (action.type) {
        // ── 1) Mudar etapa do card
        case 'change_stage': {
          let targetStageId = asId(payload.stage_id);
          if (!targetStageId && answer === 'yes') targetStageId = asId(payload.yes_stage_id);
          if (!targetStageId && answer === 'no')  targetStageId = asId(payload.no_stage_id);
          if (!targetStageId) return { ok: true, summary: 'change_stage: nenhum stage destino' };

          // Captura pendentes antes da troca (só as antigas devem ser canceladas).
          let oldPendingIds: string[] = [];
          try {
            oldPendingIds = activity.card_id
              ? await this.repo.listPendingActivityIdsForCard(activity.card_id)
              : [];
          } catch (e) {
            console.warn('[flowConfig] listar pendentes falhou:', e);
          }

          await this.repo.updateCardStage(activity.card_id ?? '', targetStageId);

          if (oldPendingIds.length > 0) {
            try { await this.repo.cancelActivitiesByIds(oldPendingIds); }
            catch (e) { console.warn('[flowConfig] auto-cancel atividades:', e); }
          }
          return { ok: true, summary: 'Card movido' };
        }

        // ── 2) Criar card novo em outra pipeline+stage
        case 'create_card': {
          const pipelineId = asId(payload.pipeline_id);
          const stageId    = asId(payload.stage_id);
          if (!pipelineId || !stageId) {
            return { ok: false, error: 'create_card: pipeline_id e stage_id obrigatórios' };
          }
          const lead = await this.repo.getLeadForCardCreation(activity.lead_id ?? '');
          if (!lead) return { ok: false, error: 'lead não encontrado' };
          // Nota: a tabela `cards` não tem coluna workspace_id (escopo vem via
          // pipeline). O código legado inseria workspace_id — removido por não
          // existir no schema (teria falhado no banco).
          await this.repo.insertCard({
            lead_id:      activity.lead_id ?? '',
            pipeline_id:  pipelineId,
            stage_id:     stageId,
            sdr_id:       lead.sdr_id,
            closer_id:    lead.closer_id,
          });
          return { ok: true, summary: 'Card criado' };
        }

        // ── 3) Criar nova atividade
        case 'create_activity': {
          const days = Number(payload.days_ahead) || 0;
          const due  = new Date();
          due.setDate(due.getDate() + days);
          await this.repo.insertActivity({
            workspace_id: activity.workspace_id ?? '',
            card_id:      activity.card_id ?? '',
            lead_id:      activity.lead_id ?? '',
            assigned_to:  activity.assigned_to,
            type:         asId(payload.activity_type) || 'outro',
            title:        asId(payload.title)          || 'Atividade',
            description:  asId(payload.description),
            due_date:     due.toISOString(),
          });
          return { ok: true, summary: 'Nova atividade criada' };
        }

        // ── 4) Atualizar campo do lead
        case 'update_lead': {
          const field = asId(payload.field);
          if (!field) return { ok: false, error: 'update_lead: field obrigatório' };
          await this.repo.updateLead(activity.lead_id ?? '', { [field]: payload.value });
          return { ok: true, summary: `Lead atualizado: ${field}` };
        }

        // ── 5) Atualizar status do agendamento
        case 'update_meeting_status': {
          const desired = String(payload.meeting_status || '').toLowerCase();
          if (!desired) return { ok: false, error: 'update_meeting_status: meeting_status obrigatório' };
          const cardId = activity.card_id ?? '';
          let meetingId = await this.repo.findMeetingIdByStatus(cardId, 'done');
          if (!meetingId) meetingId = await this.repo.findLatestMeetingId(cardId);
          if (!meetingId) return { ok: false, error: 'Sem meeting vinculada a este card' };

          const finalStatus = (desired === 'rescheduled' ? 'cancelled' : desired) as MeetingStatus;
          await this.repo.updateMeetingStatus(meetingId, finalStatus);
          if (desired === 'rescheduled') {
            await this.repo.updateCardReagend(cardId, true);
          }
          return { ok: true, summary: `Reunião → ${finalStatus}` };
        }

        default:
          return { ok: false, error: `Tipo de ação desconhecido: ${action.type}` };
      }
    } catch (e) {
      return { ok: false, error: (e as Error)?.message || String(e) };
    }
  };
}
